import { Suspense } from 'react';
import { R3fCanvas } from './components/r3f/r3f-canvas';
import { useColorsData } from './hooks/useColorsData';
import { useClusterData } from './hooks/useClusterData';
import { useImagePaletteData } from './hooks/useImagePaletteData';
import { ColorGroup } from './components/r3f/color-group/color-group';
import { CameraController } from './components/r3f/camera-controller';
import { useEffect, useRef } from 'react';
import { useExperienceStore } from './store';
import { useIsTouch } from '@/hooks/use-is-touch';
import { CaseMetaData } from './components/interface-elements/case-metadata';
import { CasePlane } from './components/r3f/case-plane';
import { Background } from './components/r3f/background';
import { useTheme } from 'next-themes';
import { CONFIG } from './config';
import { useRouter } from 'next/router';
import { Mouse } from './components/interface-elements/mouse';
import { ExperienceCase } from '@/lib/schemas/experience-case';
import { PaletteType } from './types';

export interface CaseExperienceProps {
  experienceItems: {
    slug: string;
    title: string;
    caseItems: Array<ExperienceCase>;
  }[];
  showExperience?: boolean;
}

export function CaseExperience({ experienceItems }: CaseExperienceProps) {
  const isTouch = useIsTouch();
  const { resolvedTheme } = useTheme();
  const router = useRouter();
  const workerRef = useRef<{
    invoke: (colors: string[], palette: PaletteType[]) => void;
  } | null>(null);

  const backgroundColor = resolvedTheme === 'light' ? 'white' : 'black';

  const colorsData = useColorsData(experienceItems);
  const clusterData = useClusterData();
  const imagePaletteData = useImagePaletteData(experienceItems);

  const {
    updateIsClickable,
    currentState,
    activeIndexes,
    updateActivePalette,
    activePalette,
    updateState,
    updateExploreState,
    resetActiveIndex,
  } = useExperienceStore((state) => ({
    updateIsClickable: state.updateIsClickable,
    currentState: state.currentState,
    activeIndexes: state.activeIndexes,
    updateActivePalette: state.updateActivePalette,
    activePalette: state.activePalette,
    updateState: state.updateState,
    updateExploreState: state.updateExploreState,
    resetActiveIndex: state.resetActiveIndex,
  }));

  // Run this function in a worker, so when lots of calcuclations needs to be done, it can be done on a sepearte thread.
  useEffect(() => {
    const worker = new Worker(
      new URL('./workers/nearest-palette.js', import.meta.url)
    );

    worker.onmessage = (evt) => {
      updateActivePalette(evt.data);
      updateState('loading');
    };

    workerRef.current = {
      invoke(colors, palette) {
        worker.postMessage({ colors, palette });
      },
    };

    return () => {
      worker.terminate();
    };
  }, [updateActivePalette, updateState]);

  // Set the current state to matching if the user has found the right amount of items
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (currentState === 'explore') {
      if (activeIndexes.length === CONFIG.AMOUNT_OF_ITEMS_BEFORE_MATCHING) {
        timer = setTimeout(() => {
          // Start finding the correct palette
          const colorsInSpectrum = colorsData.filter((_, index) =>
            activeIndexes.includes(index)
          );

          if (workerRef.current) {
            workerRef.current.invoke(
              colorsInSpectrum.map((item) => item.color),
              imagePaletteData
            );
          }
        }, CONFIG.CONFIRM_SELECTION_EXPLORE_IN_SECONDS * 1000);
      } else {
        updateExploreState('cluster');

        timer = setTimeout(() => {
          resetActiveIndex();
        }, CONFIG.RESET_EXPLORE_IN_SECONDS * 1000);
      }

      if (activeIndexes.length === 0) {
        updateExploreState('orb');
      }
    }

    if (currentState === 'matched') {
      timer = setTimeout(() => {
        resetActiveIndex();
        updateState('explore');
      }, CONFIG.CASE_GO_AWAY_IN_SECONDS * 1000);
    }

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndexes, currentState]);

  // Check if touch, than disable the hover
  useEffect(() => {
    if (isTouch) updateIsClickable(false);
  }, [updateIsClickable, isTouch]);

  // Reset the store when the component is unmounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      updateState('start');
      updateExploreState('orb');
      updateActivePalette(null);
      resetActiveIndex();
      document.body.style.overflow = 'visible';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <CaseMetaData />
      {currentState === 'explore' && !isTouch && <Mouse />}

      <R3fCanvas>
        <Suspense fallback={null}>
          <Background backgroundColor={backgroundColor} />

          {(currentState === 'start' ||
            currentState === 'intro' ||
            currentState === 'explore' ||
            currentState === 'loading') && (
            <ColorGroup colorsData={colorsData} clusterData={clusterData} />
          )}

          {activePalette &&
            (currentState === 'matched' ||
              currentState === 'outro' ||
              currentState === 'explore') && (
              <CasePlane
                activePalette={activePalette}
                backgroundColor={backgroundColor}
                router={router}
              />
            )}
          <CameraController />
        </Suspense>
      </R3fCanvas>
    </>
  );
}
